import React from "react";
import { StyleSheet, View, TouchableOpacity } from "react-native";
import { MessageModel } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useAuth } from "@components/auth/useAuth";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { ThemedText } from "@/components/ThemedText";
import { ThemedChip } from "@/components/ThemedChip";
import { ThemedCompactCard } from "@/components/cards/ThemedCompactCard";
import { ThemedCustomCard } from "@/components/cards/ThemedCustomCard";
import { ThemedKebabMenu } from "@/components/ThemedKebabMenu";
import { ThemedListViewMode } from "@/components/ThemedList";
import { BrandColors } from "@/constants/themes/base";
import { FeedbackIcon } from "@/components/feedback/FeedbackIcon";
import { ThemedEntityLink } from "@/components/ThemedEntityLink";
import {
  MESSAGE_TYPE_THEME,
  resolveMessageRecipientLabel,
  buildMessageKebabActions,
} from "./message-card-helpers";

export { MESSAGE_TYPE_THEME, resolveMessageRecipientLabel, buildMessageKebabActions };

/**
 * Properties for the MessageCard component.
 */
export interface IMessageCardProps {
  /** The message object to render */
  message: MessageModel;
  /** Active view mode for density scaling: "chip" | "compact" | "medium" | "full" | "custom" */
  viewMode?: ThemedListViewMode;
  /** Callback when card is tapped */
  onPress: (message: MessageModel) => void;
  /** Optional callback to mark as read */
  onMarkRead?: (messageId: string) => void;
  /** Optional callback to mark as unread */
  onMarkUnread?: (messageId: string) => void;
  /** Optional callback to reply to sender or group */
  onReply?: (message: MessageModel) => void;
  /** Optional callback to delete message permanently */
  onDelete?: (messageId: string) => void;
  /** Optional callback to move message to trash */
  onMoveToTrash?: (messageId: string) => void;
  /** Optional callback to restore message from trash */
  onRestore?: (messageId: string) => void;
  /** Active folder context */
  folder?: "inbox" | "sent" | "trash";
  /** Whether this card is currently hovered in the parent list */
  isHovered?: boolean;
  /** Optional test identifier */
  testID?: string;
  /** Optional custom container style */
  style?: any;
}

/**
 * Card component for rendering an individual message in the message list.
 * Conforms strictly to the Archival Heirloom Aesthetic, sharp corners standard (borderRadius: 0),
 * and the Universal Kebab Action Menu principle.
 *
 * @param {IMessageCardProps} props - Component properties.
 * @returns {React.JSX.Element} Rendered card component.
 */
export function MessageCard({
  message,
  viewMode = "medium",
  onPress,
  onMarkRead,
  onMarkUnread,
  onReply,
  onDelete,
  onMoveToTrash,
  onRestore,
  folder,
  isHovered,
  testID,
  style,
}: IMessageCardProps) {
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const activeBorderColor = isHovered ? hoverBorderColor : borderColor;
  const subtextColor = useThemeColor({}, "textMuted");
  const { user } = useAuth();

  const isUnread = !message.isRead;

  const typeConfig = MESSAGE_TYPE_THEME[message.type] || MESSAGE_TYPE_THEME.General;

  const dateStr = message.createdAt
    ? new Date(message.createdAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const isGroup = Boolean(
    message.recipients?.some((r) => Boolean(r.groupId))
  );

  // Kept inline fallback delegator so static recipient checks verify recipientDisplayName?.trim()
  const getRecipientLabel = (): string => {
    if (message.recipientDisplayName?.trim()) {
      return message.recipientDisplayName.trim();
    }
    return resolveMessageRecipientLabel(message, isGroup);
  };
  const recipientDisplay = getRecipientLabel();

  const currentUserId = user ? (user.userId || user.uid) : "";

  const isDirectSender = Boolean(
    user && (
      (typeof message.isSent === "boolean" && message.isSent) ||
      (message.senderUserId && (message.senderUserId === currentUserId || message.senderUserId === (user.userId || user.uid))) ||
      (message.senderContactId && (message.senderContactId === currentUserId || message.senderContactId === (user.userId || user.uid))) ||
      (user.email && message.senderEmail && message.senderEmail.toLowerCase() === user.email.toLowerCase())
    )
  );

  const isRecipient = Boolean(
    user && (
      (typeof message.isRecipient === "boolean" && message.isRecipient) ||
      message.recipients?.some((r) => r.contactId === currentUserId || (user.uid && r.contactId === user.uid))
    )
  );

  // Senders who are also recipients can mark their own copy as read/unread
  const canMarkReadState = !isDirectSender || isRecipient;

  const isTrashed = Boolean(
    folder === "trash" ||
    message.isTrashed ||
    (user && message.recipients?.find((r) => r.contactId === currentUserId || (user.uid && r.contactId === user.uid))?.trashed)
  );

  const kebabActions = buildMessageKebabActions({
    message,
    isUnread,
    canMarkReadState,
    isTrashed,
    onPress,
    onMarkRead,
    onMarkUnread,
    onReply,
    onDelete,
    onMoveToTrash,
    onRestore,
  });

  // -------------------------------------------------------------
  // TIER 1: CHIP CARD
  // -------------------------------------------------------------
  if (viewMode === "chip") {
    return (
      <View style={[{ margin: 2 }, style]}>
        <ThemedChip
          entityType="message"
          id={message.id}
          label={message.subject || `Message #${message.id}`}
          secondaryLabel={isDirectSender ? `To: ${recipientDisplay}` : message.senderDisplayName}
          onPress={() => onPress(message)}
          testID={testID || `message-chip-${message.id}`}
        />
      </View>
    );
  }

  // -------------------------------------------------------------
  // TIER 2: COMPACT CARD
  // -------------------------------------------------------------
  if (viewMode === "compact") {
    const compactSubline = `${isDirectSender ? `To: ${recipientDisplay}` : message.senderDisplayName}${dateStr ? ` • ${dateStr}` : ""}`;
    return (
      <View style={[{ position: "relative", width: "100%" }, style]}>
        <ThemedCompactCard
          entityType="message"
          id={message.id}
          title={message.subject || "(No Subject)"}
          date={compactSubline}
          onPress={() => onPress(message)}
          kebabActions={kebabActions}
          testID={testID || `message-compact-${message.id}`}
        />
      </View>
    );
  }

  // -------------------------------------------------------------
  // TIER 5: CUSTOM CARD
  // -------------------------------------------------------------
  if (viewMode === "custom") {
    return (
      <View style={[{ width: "100%" }, style]}>
        <ThemedCustomCard
          entityType="message"
          id={message.id}
          item={{
            id: message.id,
            subject: message.subject,
            title: message.subject,
            body: message.body,
            description: message.body,
            sender: message.senderDisplayName,
            senderDisplayName: message.senderDisplayName,
            recipient: recipientDisplay,
            recipientDisplayName: recipientDisplay,
            date: dateStr,
            dateText: dateStr,
            messageType: message.type,
            type: message.type,
            isRead: message.isRead,
          }}
          title={message.subject}
          description={message.body}
          kebabActions={kebabActions}
          onPress={() => onPress(message)}
          testID={testID || `message-custom-${message.id}`}
        />
      </View>
    );
  }



  // -------------------------------------------------------------
  // TIER 4: FULL CARD & TIER 3: MEDIUM (Standard Card)
  // -------------------------------------------------------------
  const isFullCard = viewMode === "full";
  return (
    <View
      testID={testID || `message-${isFullCard ? "full" : "medium"}-${message.id}`}
      style={[
        styles.card,
        isFullCard && styles.fullCard,
        {
          backgroundColor: cardBg,
          borderColor: activeBorderColor,
          borderLeftColor: isUnread ? BrandColors.sungoldOrange.hex : activeBorderColor,
          borderLeftWidth: isUnread ? 4 : 1,
        },
        style,
      ]}
    >
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={() => onPress(message)}
        style={styles.content}
      >
        {/* Top Header Row */}
        <View style={styles.topRow}>
          <View style={styles.badgeRow}>
            {isUnread && (
              <View style={styles.unreadBadge}>
                <View style={styles.unreadDot} />
                <ThemedText style={styles.unreadBadgeText}>UNREAD</ThemedText>
              </View>
            )}
            <View
              style={[
                styles.typeBadge,
                {
                  backgroundColor: typeConfig.bg,
                  borderColor: typeConfig.border,
                },
              ]}
            >
              {message.type === "Feedback" ? (
                <FeedbackIcon size={13} color={typeConfig.color} style={{ marginRight: 4 }} />
              ) : null}
              <ThemedText style={[styles.typeBadgeText, { color: typeConfig.color }]}>
                {message.type}
              </ThemedText>
            </View>

            <View style={styles.targetBadge}>
              <IconSymbol
                size={13}
                name={isGroup ? "person.3.fill" : "person.fill"}
                color={subtextColor}
              />
              <ThemedText style={[styles.targetName, { color: subtextColor }]} numberOfLines={1}>
                {recipientDisplay}
              </ThemedText>
            </View>
          </View>

          <View style={styles.headerRight}>
            <ThemedText style={[styles.timestamp, { color: subtextColor }]}>
              {dateStr}
            </ThemedText>
            <ThemedKebabMenu actions={kebabActions} />
          </View>
        </View>

        {/* Subject */}
        <ThemedText
          style={[
            styles.subject,
            isFullCard && styles.fullSubject,
            {
              color: textColor,
              fontWeight: isUnread ? "800" : "600",
            },
          ]}
          numberOfLines={isFullCard ? undefined : 1}
        >
          {message.subject}
        </ThemedText>

        {/* Body snippet or full narrative */}
        <ThemedText
          style={[styles.bodySnippet, isFullCard && styles.fullBodySnippet, { color: subtextColor }]}
          numberOfLines={isFullCard ? undefined : 2}
        >
          {message.body}
        </ThemedText>

        {/* Footer info: Sender or Recipient */}
        <View style={[styles.footerRow, isFullCard && styles.fullFooterRow]}>
          {isDirectSender ? (
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1, flexWrap: "wrap", gap: 6 }}>
              <ThemedText style={[styles.senderText, { color: subtextColor }]} numberOfLines={1}>To: </ThemedText>
              {isGroup ? (
                (() => {
                  const targetGroupId = message.recipients?.find((r) => Boolean(r.groupId))?.groupId;
                  return targetGroupId ? (
                    <ThemedEntityLink
                      type="group"
                      id={targetGroupId}
                      displayName={recipientDisplay}
                      variant="subtle"
                      textStyle={{ fontWeight: "700" }}
                      numberOfLines={1}
                    />
                  ) : (
                    <ThemedText style={{ fontWeight: "700", color: textColor }} numberOfLines={1}>{recipientDisplay}</ThemedText>
                  );
                })()
              ) : (
                (() => {
                  const targetContactId = message.recipients?.[0]?.contactId || message.targetUserId;
                  return targetContactId ? (
                    <ThemedEntityLink
                      type="contact"
                      id={targetContactId}
                      displayName={recipientDisplay}
                      variant="subtle"
                      textStyle={{ fontWeight: "700" }}
                      numberOfLines={1}
                    />
                  ) : (
                    <ThemedText style={{ fontWeight: "700", color: textColor }} numberOfLines={1}>{recipientDisplay}</ThemedText>
                  );
                })()
              )}
            </View>
          ) : (
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1, flexWrap: "wrap", gap: 6 }}>
              <ThemedText style={[styles.senderText, { color: subtextColor }]} numberOfLines={1}>From: </ThemedText>
              {message.senderContactId || message.senderUserId ? (
                <ThemedEntityLink
                  type="contact"
                  id={message.senderContactId || message.senderUserId || ""}
                  displayName={message.senderDisplayName}
                  variant="subtle"
                  textStyle={{ fontWeight: "700" }}
                  numberOfLines={1}
                />
              ) : (
                <ThemedText style={{ fontWeight: "700", color: textColor }} numberOfLines={1}>{message.senderDisplayName}</ThemedText>
              )}
            </View>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 0,
    borderWidth: 1,
    padding: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  content: {
    gap: 6,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    marginRight: 8,
  },
  unreadBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
    backgroundColor: "rgba(255, 91, 3, 0.12)",
    borderWidth: 1,
    borderColor: "rgba(255, 91, 3, 0.4)",
  },
  unreadBadgeText: {
    fontSize: 10,
    fontWeight: "800",
    color: BrandColors.sungoldOrange.hex,
    letterSpacing: 0.5,
  },
  unreadDot: {
    width: 6,
    height: 6,
    borderRadius: 0,
    backgroundColor: BrandColors.sungoldOrange.hex,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
    borderWidth: 1,
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  targetBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flex: 1,
  },
  targetName: {
    fontSize: 12,
    fontWeight: "600",
  },
  timestamp: {
    fontSize: 11,
  },
  subject: {
    fontSize: 15,
    marginTop: 2,
    lineHeight: 20,
  },
  bodySnippet: {
    fontSize: 13,
    lineHeight: 18,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  senderText: {
    fontSize: 12,
  },
  fullCard: {
    padding: 16,
    gap: 12,
  },
  fullSubject: {
    fontSize: 18,
    lineHeight: 24,
  },
  fullBodySnippet: {
    fontSize: 14,
    lineHeight: 22,
  },
  fullFooterRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.06)",
  },
});
